import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "../../elements/NewCarousel/NewCarouselArrows";
import NewCardArticle from "@elements/NewCardArticle";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import { buildThemeClasses } from "@src/utils/theme";
import clsx from "clsx";
import { useLiveUpdates } from "@src/hooks/useLiveUpdates";
import { getContentfulPreviewPropsFn } from "@utils";

export type ArticleProps = {
  contentful_id: string;
  __typename: string;
  id: string;
  title: string;
  slug: string;
  type: string;
  heroImage: {
    title: string;
    gatsbyImageData?: object;
  };
  description?: {
    description?: string;
  };
};

type NewLatestArticlesProps = {
  entry: {
    contentful_id: string;
    overline: string;
    title: string;
    subtitle: string;
    maxArticles: number;
    customTheme?: string;
  };
  extra?: {
    useDropdownButton?: boolean;
    articlesList?: ArticleProps[];
  };
  className?: string;
};

const NewLatestArticles = ({
  entry: rawEntry,
  extra = {},
  className,
}: NewLatestArticlesProps): JSX.Element => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start" });
  const entry = useLiveUpdates(rawEntry) as NewLatestArticlesProps["entry"];

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);
  const {
    overline = "STAY UPDATED",
    title = "See the latest reputation insights",
    subtitle,
    maxArticles = 10,
    contentful_id = "latest-publications",
    customTheme,
  } = entry;

  const { articlesList } = extra;

  const newArticlesList = maxArticles
    ? articlesList?.filter((_, index: number) => maxArticles > index)
    : articlesList;

  const getPreviewProps = getContentfulPreviewPropsFn(contentful_id);

  return (
    <section
      id={contentful_id}
      className={clsx(
        "py-section",
        className,
        buildThemeClasses(customTheme)
      )}
      {...getPreviewProps("moduleName")}
    >
      <IdTooltip id={contentful_id} />
      <div className="inner">
        <div className="flex items-end justify-between mb-10">
          <div className="flex flex-col gap-2">
            {overline && (
              <p
                {...getPreviewProps("overline")}
              >
                {overline}
              </p>
            )}
            {title && (
              <h2
                className="text-rt-xl leading-none"
                {...getPreviewProps("title")}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p {...getPreviewProps("subtitle")}>
                {subtitle}
              </p>
            )}
          </div>
          <div className="gap-4 hidden md:flex">
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              theme={customTheme}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              theme={customTheme}
            />
          </div>
        </div>
        <div ref={emblaRef} className="hidden md:block overflow-hidden">
          <div className="flex touch-pan-y -ml-8">
            {newArticlesList?.map((article: ArticleProps, index: number) => (
              <NewCardArticle
                key={article.contentful_id}
                article={article}
                childClassName="md:flex-33% lg:flex-25% xl:flex-20% pl-8"
                theme={customTheme}
              />
            ))}
          </div>
        </div>
        <div className="-mx-4 px-4 overflow-x-auto md:hidden">
          <div className="flex -ml-4">
            {newArticlesList?.map((article: ArticleProps, index: number) => (
              <NewCardArticle
                key={article.contentful_id}
                article={article}
                childClassName="flex-66% pl-4"
                theme={customTheme}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewLatestArticles;
